/* THE ADD-ONS PANEL (upsell test version, 22 Oct 2026). Markup, words and settings: sections/v4-upsell.liquid.

   Josh: "this CANNOT break and stop people ordering". So:
   - It opens only after Shopify has put the light in the basket. v4-product.js calls window.v4Upsell.open(result,
     options) from its success branch; open() returns true when the panel is on screen and false in every other case
     (no panel on the page, its styles missing, nothing left to offer, any error). On false, v4-product.js goes to the
     basket exactly as before.
   - Go to basket is an ordinary link, so it works whatever happens to this script once the panel is open.
   - An add-on goes into the basket only when its own Add button is pressed, one at a time, each with a 15-second
     limit. A failure shows a message on that add-on and leaves everything else working.

   Written for older Safari too (iOS 12 up): no arrow functions, optional chaining, replaceChildren, Object.hasOwn,
   flex gap or the dialog element. */
(function () {
  'use strict';

  var root = null;
  var sheet = null;
  var isOpen = false;
  var openedAt = 0;
  var lastFocus = null;
  var onLeave = null;
  var closeTimer = null;
  var lockY = 0;
  var bodyStyle = null;
  var madeInert = [];
  var text = {};
  var current = { light: '', offered: 0 };

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function find(sel, scope) { return (scope || root).querySelector(sel); }
  function findAll(sel, scope) { return (scope || root).querySelectorAll(sel); }
  function trim(value) { return String(value == null ? '' : value).replace(/^\s+|\s+$/g, ''); }
  function designMode() { return !!(window.Shopify && window.Shopify.designMode); }
  function stillMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Every basket request the panel makes waits for the one before it, so two can never cross and undo each other.
  var queue = Promise.resolve();
  function inTurn(job) {
    var run = queue.then(job, job);
    queue = run.catch(function () { /* the next request still runs */ });
    return run;
  }

  // Every request has a time limit, so a weak signal can never leave a button on "Adding" or hold up the queue.
  // Browsers without AbortController (Safari before 12.1) stop waiting at the limit even though they cannot cancel.
  function send(url, options, limit) {
    var controller = window.AbortController ? new window.AbortController() : null;
    var timer = null;
    if (controller) options.signal = controller.signal;
    var late = new Promise(function (resolve, reject) {
      timer = window.setTimeout(function () {
        if (controller) { try { controller.abort(); } catch (e) { /* already finished */ } }
        var error = new Error('timeout');
        error.name = 'AbortError';
        reject(error);
      }, limit);
    });
    return Promise.race([fetch(url, options), late]).then(
      function (response) { window.clearTimeout(timer); return response; },
      function (error) { window.clearTimeout(timer); throw error; }
    );
  }

  // GA4 hears page events through the theme's Google tag (only there after marketing consent); Shopify's customer
  // events hear them through analytics.publish. Neither may ever get in the way of the panel.
  function track(name, params) {
    params = params || {};
    try { if (typeof window.gtag === 'function') window.gtag('event', name, params); } catch (e) { /* never in the way */ }
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish(name, params);
    } catch (e) { /* never in the way */ }
  }

  function setup() {
    if (root && document.documentElement.contains(root)) return true;
    root = document.querySelector('[data-v4-upsell]');
    if (!root) return false;
    sheet = find('.v4-up-sheet');
    if (!sheet) { root = null; return false; }
    text = {
      added: root.getAttribute('data-added') || 'Added',
      adding: root.getAttribute('data-adding') || 'Adding',
      error: root.getAttribute('data-error') || 'We could not add that. Please try again.',
      soldOut: root.getAttribute('data-sold-out') || 'Sorry, that has just sold out.',
      cartAdd: root.getAttribute('data-cart-add') || '/cart/add',
      cartUpdate: root.getAttribute('data-cart-update') || '/cart/update',
      cart: root.getAttribute('data-cart') || '/cart',
      currency: root.getAttribute('data-currency') || 'GBP'
    };
    // Outside the page's main content, so the rest of the page can be shut while the panel is open. In the theme editor
    // it stays inside its section, where the editor looks for it.
    if (!designMode() && root.parentNode !== document.body) document.body.appendChild(root);
    if (!root.hasAttribute('data-v4-up-wired')) {
      root.setAttribute('data-v4-up-wired', '');
      root.addEventListener('click', onClick);
      root.addEventListener('change', onChange);
    }
    return true;
  }

  // The panel's own stylesheet loads before this script (sections/v4-upsell.liquid). If it has not arrived, the panel
  // would draw as plain text at the foot of the page, so it does not open and the shopper goes to the basket instead.
  function stylesReady() {
    try { return window.getComputedStyle(root).position === 'fixed'; } catch (e) { return false; }
  }

  // The light's chosen option values, lower case: "black", "heavyweight", "cordless".
  function choiceValues(item) {
    var out = [];
    if (item && item.options_with_values && item.options_with_values.length) {
      each(item.options_with_values, function (option) {
        if (option && option.value != null) out.push(trim(option.value).toLowerCase());
      });
    } else if (item && item.variant_options && item.variant_options.length) {
      each(item.variant_options, function (value) { if (value != null) out.push(trim(value).toLowerCase()); });
    }
    return out;
  }

  // The light's finish: 'white' when one of its chosen values says white (Heritage White), 'black' when one says black,
  // '' when it has none (the Gemini).
  function finishOf(values) {
    var i;
    for (i = 0; i < values.length; i++) if (values[i].indexOf('white') !== -1) return 'white';
    for (i = 0; i < values.length; i++) if (values[i].indexOf('black') !== -1) return 'black';
    return '';
  }

  // Shows each add-on unless it is drawn for the other finish or the light's choice already includes it, puts every Add
  // button back to "Add", and returns how many add-ons are on offer.
  function prepare(item, ignoreRules) {
    var chosen = choiceValues(item);
    var finish = finishOf(chosen) || 'black';
    var shown = 0;
    each(findAll('[data-v4-up-item]'), function (li) {
      // An add-on drawn for one finish (the light engine, the diffuser ring, the cable tidy) shows only with a light of
      // that finish; a light without a finish gets the black one (Josh, 22 Oct: "needs to be correct depending on
      // whether they picked white or black lights").
      var itemFinish = li.getAttribute('data-finish') || '';
      var off = !!itemFinish && itemFinish !== finish;
      if (!off && !ignoreRules) {
        var rules = (li.getAttribute('data-hide') || '').split(',');
        for (var i = 0; i < rules.length; i++) {
          var rule = trim(rules[i]).toLowerCase();
          if (rule && chosen.indexOf(rule) !== -1) { off = true; break; }
        }
      }
      li.hidden = off;
      setState(li, 'ready');
      if (!off) shown += 1;
    });
    var list = find('[data-v4-up-list]');
    if (list) list.setAttribute('data-count', String(shown));
    // Four or more add-ons get the compact layout (v4-upsell.css), so the panel still fits on one screen.
    root.setAttribute('data-count', String(shown));
    root.classList.toggle('is-many', shown >= 4);
    root.classList.toggle('is-lots', shown >= 5);
    return shown;
  }

  function sized(url, width) {
    url = String(url);
    if (!/(cdn\.shopify\.com|\/cdn\/shop\/)/.test(url) || /[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function money(cents) {
    var format = (root.getAttribute('data-money') || '£{{amount}}').replace(/<[^>]*>/g, '');
    var amount = (Number(cents) || 0) / 100;
    function grouped(places) {
      var parts = amount.toFixed(places).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, kind) {
      if (kind === 'amount_no_decimals') return grouped(0);
      if (kind === 'amount_with_comma_separator') return amount.toFixed(2).replace('.', ',');
      return grouped(2);
    });
  }

  // The light that has just gone in: its name, the choice ("Black · Heavyweight · Mains"), the price the page was
  // showing (the offer price when a code is on) and its photo.
  function fillLight(item, opts) {
    var nameEl = find('[data-v4-up-light-name]');
    var choiceEl = find('[data-v4-up-light-choice]');
    var priceEl = find('[data-v4-up-light-price]');
    var img = find('[data-v4-up-light-img]');
    var name = trim(item.product_title || item.title || root.getAttribute('data-light-name'));
    if (nameEl && name) nameEl.textContent = name;
    var choice = item.variant_title && item.variant_title !== 'Default Title' ? String(item.variant_title).split(' / ').join(' · ') : '';
    if (choiceEl) { choiceEl.textContent = choice; choiceEl.hidden = !choice; }
    var price = trim(String(opts.price || '').replace(/\s+/g, ' '));
    if (!price && typeof item.final_price === 'number') price = money(item.final_price);
    if (priceEl) { priceEl.textContent = price; priceEl.hidden = !price; }
    // When the light's page says which picture shows exactly the finish and base chosen (exactImage), only that picture is
    // used, or none: the basket's own photo is the product's main one whatever was chosen (23 Oct 2026, final check).
    var src = opts.exactImage ? String(opts.image || '')
      : ((item.featured_image && item.featured_image.url) || item.image || root.getAttribute('data-light-image') || '');
    if (img) {
      if (src) { img.src = sized(src, 720); img.hidden = false; } else img.hidden = true;
      // No picture box at all without a picture: an empty grey square would look broken.
      if (img.parentNode && img.parentNode.classList) img.parentNode.classList.toggle('is-empty', !src);
    }
    current.light = name;
  }

  // The add-on photos wait until someone shows they are about to buy (their pointer or finger reaches Add to basket),
  // then load, so the panel opens with its pictures already there and nobody else downloads them.
  function warm() {
    if (!root) return;
    each(findAll('img[loading="lazy"]'), function (img) { img.loading = 'eager'; });
  }

  function warmOnIntent() {
    var form = document.getElementById('v4-product-form');
    var button = form && form.querySelector('[type="submit"]');
    if (!button) return;
    var go = function () { if (setup()) warm(); };
    ['pointerdown', 'mouseenter', 'touchstart', 'focus'].forEach(function (type) {
      button.addEventListener(type, go, { passive: true, once: true });
    });
  }

  function lockPage() {
    if (designMode()) return;
    var style = document.body.style;
    lockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    bodyStyle = { position: style.position, top: style.top, left: style.left, right: style.right, width: style.width };
    style.position = 'fixed';
    style.top = -lockY + 'px';
    style.left = '0';
    style.right = '0';
    style.width = '100%';
  }

  function unlockPage() {
    if (!bodyStyle) return;
    var style = document.body.style;
    style.position = bodyStyle.position;
    style.top = bodyStyle.top;
    style.left = bodyStyle.left;
    style.right = bodyStyle.right;
    style.width = bodyStyle.width;
    bodyStyle = null;
    var html = document.documentElement;
    var smooth = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, lockY);
    html.style.scrollBehavior = smooth;
  }

  // While the panel is open the rest of the page cannot be reached by keyboard or screen reader. Only what this script
  // shut is opened again.
  function shutPage(on) {
    if (designMode()) return;
    if (on) {
      madeInert = [];
      each(document.body.children, function (el) {
        if (el === root || el.nodeName === 'SCRIPT' || el.nodeName === 'STYLE' || el.nodeName === 'LINK') return;
        if ('inert' in el) {
          if (!el.inert) { el.inert = true; madeInert.push([el, 'inert']); }
        } else if (el.getAttribute('aria-hidden') !== 'true') {
          el.setAttribute('aria-hidden', 'true');
          madeInert.push([el, 'aria']);
        }
      });
    } else {
      each(madeInert, function (pair) {
        if (pair[1] === 'inert') pair[0].inert = false;
        else pair[0].removeAttribute('aria-hidden');
      });
      madeInert = [];
    }
  }

  function show() {
    if (closeTimer) { window.clearTimeout(closeTimer); closeTimer = null; }
    lastFocus = document.activeElement;
    lockPage();
    root.hidden = false;
    document.documentElement.classList.add('v4-up-on');
    void root.offsetWidth; // draws the closed state first, so the panel slides in rather than appearing
    root.classList.add('is-open');
    isOpen = true;
    openedAt = Date.now();
    shutPage(true);
    try { sheet.focus({ preventScroll: true }); } catch (e) { sheet.focus(); }
  }

  function hide(instant) {
    if (!isOpen) return false;
    isOpen = false;
    root.classList.remove('is-open');
    shutPage(false);
    document.documentElement.classList.remove('v4-up-on');
    unlockPage();
    var done = function () { closeTimer = null; if (!isOpen) root.hidden = true; };
    if (instant || stillMotion()) done();
    else closeTimer = window.setTimeout(done, 340);
    // Keyboard focus goes back to where it was, which is Add to basket; if the browser let go of it while that button
    // was busy, it goes to Add to basket itself. Safari never puts focus on a button that is tapped or clicked: it puts it
    // on the page's main area instead, so anything that is not a control also goes back to Add to basket.
    var back = lastFocus;
    lastFocus = null;
    if (!back || back.disabled || !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(back.nodeName || '') || !document.documentElement.contains(back)) {
      back = document.querySelector('#v4-product-form [type="submit"]') || back;
    }
    if (back && back.focus && document.documentElement.contains(back)) {
      try { back.focus({ preventScroll: true }); } catch (e) { try { back.focus(); } catch (x) { /* nothing to return to */ } }
    }
    return true;
  }

  function close(reason) {
    if (hide(false)) track('upsell_close', { reason: reason || 'close', light: current.light });
  }

  function trap(event) {
    var focusable = Array.prototype.filter.call(
      sheet.querySelectorAll('a[href], button:not([disabled]), select:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.closest('[hidden]') && !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }
    );
    if (!focusable.length) { event.preventDefault(); sheet.focus(); return; }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;
    if (event.shiftKey && (active === first || active === sheet || !sheet.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !sheet.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKey(event) {
    if (!isOpen) return;
    var key = event.key || event.keyCode;
    if (key === 'Escape' || key === 'Esc' || key === 27) { event.preventDefault(); close('escape'); return; }
    if (key === 'Tab' || key === 9) trap(event);
  }

  function say(message) {
    var live = find('[data-v4-up-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(function () { live.textContent = message; }, 60);
  }

  function setState(li, state) {
    var button = find('[data-v4-up-add]', li);
    var label = find('[data-v4-up-add-text]', li);
    var select = find('select[data-v4-up-variant]', li);
    var error = find('[data-v4-up-item-error]', li);
    li.setAttribute('data-state', state);
    li.classList.toggle('is-added', state === 'added');
    li.classList.toggle('is-busy', state === 'busy');
    if (button) {
      if (!button.hasAttribute('data-label')) button.setAttribute('data-label', label ? label.textContent : '');
      if (label) label.textContent = state === 'added' ? text.added : state === 'busy' ? text.adding : button.getAttribute('data-label');
      button.setAttribute('aria-busy', state === 'busy' ? 'true' : 'false');
      button.setAttribute('aria-disabled', state === 'added' || state === 'busy' ? 'true' : 'false');
    }
    if (select) select.disabled = state === 'added' || state === 'busy';
    if (error) { error.hidden = true; error.textContent = ''; }
  }

  function failed(li, message) {
    setState(li, 'error');
    var error = find('[data-v4-up-item-error]', li);
    if (error) { error.textContent = message; error.hidden = false; }
  }

  function addedCount() {
    var n = 0;
    each(findAll('[data-v4-up-item]'), function (li) { if (li.getAttribute('data-state') === 'added') n += 1; });
    return n;
  }

  // The header's basket count, which the old path never needed because it left for the basket page. The basket page
  // always has the true figure; this is only so the count is right if the shopper carries on browsing.
  function setCount(count) {
    var link = document.getElementById('cart-icon-bubble');
    if (!link || typeof count !== 'number') return;
    var badge = link.querySelector('.sr-cart-count');
    if (!badge && count > 0) {
      badge = document.createElement('span');
      badge.className = 'sr-cart-count';
      badge.setAttribute('aria-hidden', 'true');
      link.appendChild(badge);
      var words = document.createElement('span');
      words.className = 'visually-hidden';
      words.setAttribute('data-v4-up-count', '');
      link.appendChild(words);
    }
    if (badge) badge.textContent = String(count);
    var spoken = link.querySelector('[data-v4-up-count]') || (badge && badge.nextElementSibling && badge.nextElementSibling.classList.contains('visually-hidden') ? badge.nextElementSibling : null);
    if (spoken) spoken.textContent = count === 1 ? '1 item' : count + ' items';
  }

  function refreshCount() {
    var url = text.cart.replace(/\/$/, '') + '.js';
    inTurn(function () {
      return send(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }, 8000)
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (cart) { if (cart && typeof cart.item_count === 'number') setCount(cart.item_count); });
    }).catch(function () { /* a nicety only */ });
  }

  // A note on the order, for counting what the panel sold: admin shows it under Additional details and the order export
  // has it in Note Attributes. "Add-ons panel: Shown" when the panel opened, and "Add-ons panel: Remote control: Added"
  // for each add-on added from it. The add-on's own basket line also carries the hidden property _upsell.
  function note(fields) {
    var url = text.cartUpdate.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
    inTurn(function () {
      return send(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: fields })
      }, 8000);
    }).catch(function () { /* the line property still marks the add-on */ });
  }

  function addOne(button) {
    var li = button.closest('[data-v4-up-item]');
    if (!li) return;
    var state = li.getAttribute('data-state');
    if (state === 'busy' || state === 'added') return;
    var input = find('[data-v4-up-variant]', li);
    var id = input ? parseInt(input.value, 10) : NaN;
    var name = li.getAttribute('data-name') || '';
    if (!id) { failed(li, text.error); return; }
    var option = input && input.options ? input.options[input.selectedIndex] : input;
    var cents = option ? parseInt(option.getAttribute('data-cents'), 10) : NaN;
    setState(li, 'busy');
    inTurn(function () {
      return send(text.cartAdd, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1, properties: { _upsell: 'lux-panel' } }] })
      }, 15000).then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (result) {
          if (!response.ok || (result && result.status)) {
            var refused = new Error((result && (result.description || result.message)) || '');
            refused.status = (result && result.status) || response.status;
            throw refused;
          }
          return result;
        });
      });
    })
      .then(function () {
        setState(li, 'added');
        say(text.added + ': ' + name);
        var fields = {};
        fields['Add-ons panel: ' + name] = 'Added';
        note(fields);
        refreshCount();
        var params = { light: current.light, item_name: name, variant_id: id };
        if (!isNaN(cents)) { params.value = cents / 100; params.currency = text.currency; }
        track('upsell_add', params);
      })
      .catch(function (error) {
        var soldOut = !!(error && error.status === 422);
        failed(li, soldOut ? (error.message || text.soldOut) : text.error);
        track('upsell_add_failed', { light: current.light, item_name: name, status: (error && error.status) || 0 });
      });
  }

  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    // A double tap on Add to basket must not reach the panel. When the basket answers quickly, the second tap lands while
    // the panel slides in: on the dark backdrop, which shut it before anyone saw it, or on an add-on's Add (red team,
    // 22 Oct 2026, Safari on a phone). For the first moment after it opens, a tap on the panel does nothing.
    if (Date.now() - openedAt < 600) { event.preventDefault(); return; }
    var add = target.closest('[data-v4-up-add]');
    if (add) { event.preventDefault(); addOne(add); return; }
    var basket = target.closest('[data-v4-up-basket]');
    if (basket) {
      track('upsell_basket', { light: current.light, added: addedCount() });
      if (onLeave) { try { onLeave(); } catch (e) { /* the link still goes to the basket */ } }
      return;
    }
    var shut = target.closest('[data-v4-up-close]');
    if (shut) {
      event.preventDefault();
      close(shut.hasAttribute('data-v4-up-keep') ? 'continue' : shut.classList.contains('v4-up-backdrop') ? 'outside' : 'close');
    }
  }

  function onChange(event) {
    var select = event.target;
    if (!select || !select.matches || !select.matches('select[data-v4-up-variant]')) return;
    var li = select.closest('[data-v4-up-item]');
    var option = select.options[select.selectedIndex];
    var price = li && find('[data-v4-up-price]', li);
    if (price && option && option.getAttribute('data-price')) price.textContent = option.getAttribute('data-price');
  }

  function open(result, opts) {
    try {
      opts = opts || {};
      if (isOpen || !setup() || !stylesReady()) return false;
      var item = (result && result.items && result.items[0]) || result || {};
      var offered = prepare(item, false);
      if (!offered) return false;
      fillLight(item, opts);
      onLeave = typeof opts.onLeave === 'function' ? opts.onLeave : null;
      current.offered = offered;
      warm();
      show();
      note({ 'Add-ons panel': 'Shown' });
      refreshCount();
      track('upsell_view', { light: current.light, offered: offered });
      return true;
    } catch (e) {
      try { if (isOpen) hide(true); } catch (x) { /* the basket path still runs */ }
      return false;
    }
  }

  // In the theme editor, selecting the panel's section or one of its add-ons opens it with this page's light, so the
  // words and add-ons can be seen while they are edited.
  function editorOpen(event) {
    if (!designMode()) return;
    var target = event.target;
    if (!target || !target.querySelector) return;
    var mine = (target.closest && target.closest('[data-v4-upsell]')) || target.querySelector('[data-v4-upsell]');
    if (!mine) return;
    root = null;
    if (!setup()) return;
    prepare({}, true);
    var price = document.getElementById('p-price');
    fillLight({}, { price: price ? price.textContent : '' });
    if (!isOpen) show();
  }

  window.v4Upsell = {
    open: open,
    close: function () { close('script'); },
    isOpen: function () { return isOpen; }
  };

  document.addEventListener('keydown', onKey, true);
  window.addEventListener('pageshow', function (event) { if (event.persisted && isOpen) hide(true); });
  document.addEventListener('shopify:section:select', editorOpen);
  document.addEventListener('shopify:block:select', editorOpen);
  document.addEventListener('shopify:section:deselect', function () { if (isOpen) hide(true); });
  document.addEventListener('shopify:section:load', function () { if (isOpen) hide(true); root = null; setup(); });

  function init() {
    setup();
    warmOnIntent();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
